import { latLngToLocalMeters } from '@roadworld/map-generator';
import type { ChunkPayload, OsmLanduseArea } from '@roadworld/shared';
import type { HeightfieldData } from './geometry.js';
import { sampleHeightfield } from './heightfield.js';

export interface TreeInstance {
  /** World coords in chunk-local frame. y is the base of the trunk on the terrain. */
  x: number;
  y: number;
  z: number;
  /** Uniform scale (height of the tree ≈ 5 * scale). */
  scale: number;
  /** Yaw rotation in radians. */
  yaw: number;
}

export interface VegetationResult {
  trees: TreeInstance[];
}

export interface VegetationOptions {
  /** Approx meters between trees in a forest. Larger = sparser. */
  forestSpacing: number;
  /** Hard cap on trees per chunk to keep instance counts bounded. */
  maxTreesPerChunk: number;
}

export const DEFAULT_VEGETATION_OPTIONS: VegetationOptions = {
  forestSpacing: 7,
  maxTreesPerChunk: 4000,
};

export function buildVegetation(
  payload: ChunkPayload,
  heightfield: HeightfieldData,
  options: VegetationOptions = DEFAULT_VEGETATION_OPTIONS,
): VegetationResult {
  const trees: TreeInstance[] = [];
  const half = payload.sizeMeters * 0.5;

  // Individual tree nodes — exact placement.
  for (const t of payload.osm.trees) {
    const { x, z } = latLngToLocalMeters(t.lat, t.lng, payload.origin.lat, payload.origin.lng);
    if (Math.abs(x) > half || Math.abs(z) > half) continue;
    const y = sampleHeightfield(heightfield, x, z);
    const r = pseudoRandom(t.id);
    trees.push({ x, y, z, scale: 0.8 + r * 0.6, yaw: pseudoRandom(t.id ^ 0x9e3779b9) * Math.PI * 2 });
    if (trees.length >= options.maxTreesPerChunk) {
      return { trees };
    }
  }

  // Forest polygons — jittered grid scatter.
  for (const forest of payload.osm.forests) {
    if (trees.length >= options.maxTreesPerChunk) break;
    scatterForest(forest, payload, heightfield, options, trees);
  }

  return { trees };
}

function scatterForest(
  forest: OsmLanduseArea,
  payload: ChunkPayload,
  heightfield: HeightfieldData,
  options: VegetationOptions,
  out: TreeInstance[],
): void {
  const ring: Array<{ x: number; z: number }> = [];
  for (const id of forest.outer) {
    const node = payload.osm.nodes[id];
    if (!node) continue;
    const { x, z } = latLngToLocalMeters(node.lat, node.lng, payload.origin.lat, payload.origin.lng);
    ring.push({ x, z });
  }
  if (ring.length < 3) return;

  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const p of ring) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }

  const half = payload.sizeMeters * 0.5;
  minX = Math.max(minX, -half);
  maxX = Math.min(maxX, half);
  minZ = Math.max(minZ, -half);
  maxZ = Math.min(maxZ, half);

  const spacing = options.forestSpacing;
  let seed = forest.id >>> 0;
  for (let z = minZ; z <= maxZ; z += spacing) {
    for (let x = minX; x <= maxX; x += spacing) {
      if (out.length >= options.maxTreesPerChunk) return;
      seed = (seed * 1664525 + 1013904223) >>> 0;
      const jx = x + ((seed & 0xffff) / 0xffff - 0.5) * spacing * 0.8;
      seed = (seed * 1664525 + 1013904223) >>> 0;
      const jz = z + ((seed & 0xffff) / 0xffff - 0.5) * spacing * 0.8;
      if (!pointInPolygon(jx, jz, ring)) continue;
      const y = sampleHeightfield(heightfield, jx, jz);
      seed = (seed * 1664525 + 1013904223) >>> 0;
      const scale = 0.7 + ((seed & 0xffff) / 0xffff) * 0.6;
      seed = (seed * 1664525 + 1013904223) >>> 0;
      const yaw = ((seed & 0xffff) / 0xffff) * Math.PI * 2;
      out.push({ x: jx, y, z: jz, scale, yaw });
    }
  }
}

function pointInPolygon(x: number, z: number, ring: Array<{ x: number; z: number }>): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i]!;
    const b = ring[j]!;
    const intersect =
      a.z > z !== b.z > z &&
      x < ((b.x - a.x) * (z - a.z)) / ((b.z - a.z) || 1e-9) + a.x;
    if (intersect) inside = !inside;
  }
  return inside;
}

function pseudoRandom(seed: number): number {
  const h = Math.sin(seed * 12.9898) * 43758.5453;
  return h - Math.floor(h);
}
